import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiPencil, HiTrash, HiSearch, HiChevronLeft, HiChevronRight, HiInformationCircle, HiChevronDown } from 'react-icons/hi';
import EditOrderIcon from '../../assets/icons/edit-order.svg';
import Toast from '../../components/toaster';
import { uploadImage } from '../../services/assetService';
import { createAchievement, fetchAchievementList, updateAchievement, deleteAchievement } from '../../services/achievementService';
import { fetchModuleList } from '../../services/moduleService';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

// Cache for achievements data (mirrors module list cache + optional client-side pagination)
const achievementCache = {
  achievements: null,
  allAchievements: null,
  page: 1,
  totalRecords: 0,
  hasServerTotal: false,
  suppressOptimisticNext: false,
  lastSearch: '',
  firstLoad: true,
  useClientPagination: false,
};

const Achievement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState({
    id: null,
    title: '',
    description: '',
    modulesCompleted: 0,
    logo_url: '',
    order: 1,
    is_active: true,
  });
  const [newAchievement, setNewAchievement] = useState({
    title: '',
    description: '',
    modulesCompleted: 0,
    logo_url: '',
    order: 1,
  });
  
  // Logo upload states for add modal
  const [addLogoSource, setAddLogoSource] = useState('url'); // 'url' or 'file'
  const [addLogoFile, setAddLogoFile] = useState(null);
  const [addLogoUploadedUrl, setAddLogoUploadedUrl] = useState('');
  const [uploadingAddLogo, setUploadingAddLogo] = useState(false);
  const [addLogoUploadError, setAddLogoUploadError] = useState('');
  
  // Logo upload states for edit modal
  const [editLogoSource, setEditLogoSource] = useState('url'); // 'url' or 'file'
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editLogoUploadedUrl, setEditLogoUploadedUrl] = useState('');
  const [uploadingEditLogo, setUploadingEditLogo] = useState(false);
  const [editLogoUploadError, setEditLogoUploadError] = useState('');
  
  // Validation errors
  const [addModulesCompletedError, setAddModulesCompletedError] = useState('');
  const [editModulesCompletedError, setEditModulesCompletedError] = useState('');
  const [addTitleError, setAddTitleError] = useState('');
  const [addDescriptionError, setAddDescriptionError] = useState('');
  const [editTitleError, setEditTitleError] = useState('');
  const [editDescriptionError, setEditDescriptionError] = useState('');
  const [addImageError, setAddImageError] = useState('');
  const [editImageError, setEditImageError] = useState('');
  
  // Dropdown states
  const [addDropdownOpen, setAddDropdownOpen] = useState(false);
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const addDropdownRef = useRef(null);
  const editDropdownRef = useRef(null);
  
  const [achievementToDelete, setAchievementToDelete] = useState(null);
  const [reorderAchievements, setReorderAchievements] = useState([]);

  const [reorderSaveLoading, setReorderSaveLoading] = useState(false);
  
  // Search and Pagination states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(achievementCache.page || 1);
  const [totalRecords, setTotalRecords] = useState(achievementCache.totalRecords || 0);
  const [hasServerTotal, setHasServerTotal] = useState(Boolean(achievementCache.hasServerTotal));
  const [suppressOptimisticNext, setSuppressOptimisticNext] = useState(
    Boolean(achievementCache.suppressOptimisticNext)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [achievements, setAchievements] = useState(achievementCache.achievements || []);
  const [totalModules, setTotalModules] = useState(0);
  const itemsPerPage = 16;
  
  // Guard flags to prevent duplicate API calls
  const isInitialMountRef = useRef(true);
  const isLoadingRef = useRef(false);
  const lastCallParamsRef = useRef({ page: null, search: null });
  const hasFetchedModulesRef = useRef(false);
  const isFetchingModulesRef = useRef(false);
  const loadingDelayTimerRef = useRef(null);

  const clearAchievementListCache = (options = {}) => {
    const { keepSuppressOptimisticNext = false } = options;
    achievementCache.achievements = null;
    achievementCache.allAchievements = null;
   
    achievementCache.firstLoad = true;
    achievementCache.useClientPagination = false;
    achievementCache.hasServerTotal = false;
    if (!keepSuppressOptimisticNext) {
      achievementCache.suppressOptimisticNext = false;
    }
    setHasServerTotal(false);
    if (!keepSuppressOptimisticNext) {
      setSuppressOptimisticNext(false);
    }
  };

  const extractAchievementRows = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data?.docs)) return res.data.docs;
    if (Array.isArray(res?.data?.list)) return res.data.list;
    if (Array.isArray(res?.data?.achievements)) return res.data.achievements;
    if (Array.isArray(res?.achievements)) return res.achievements;
    if (Array.isArray(res?.result)) return res.result;
    if (Array.isArray(res?.list)) return res.list;
    return [];
  };

  // Debounce search query
  const searchDebounceTimerRef = useRef(null);
  
  useEffect(() => {
    // Clear existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    // If search query is empty, update immediately (no debounce)
    if (searchQuery.trim() === '') {
      setDebouncedSearch('');
      setCurrentPage(1); // Reset to first page on search
      return;
    }

    // Otherwise, debounce the search with 500ms delay
    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1); // Reset to first page on search
    }, 500);

    // Cleanup function to clear timer on unmount or when searchQuery changes
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Load achievements when page or search changes (aligned with module list useEffect)
  useEffect(() => {
    const currentParams = { page: currentPage, search: debouncedSearch };
    const lastParams = lastCallParamsRef.current;

    const isSearchChange = debouncedSearch !== (achievementCache.lastSearch || '');
    const hasClientListCache =
      achievementCache.allAchievements !== null && !achievementCache.firstLoad && !isSearchChange;
    const useClientPagination = achievementCache.useClientPagination;

    if (
      hasClientListCache &&
      useClientPagination &&
      !isSearchChange &&
      achievementCache.allAchievements.length > itemsPerPage
    ) {
      setIsLoading(true);
      if (loadingDelayTimerRef.current) {
        clearTimeout(loadingDelayTimerRef.current);
        loadingDelayTimerRef.current = null;
      }
      const startIndex = (currentParams.page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedAchievements = achievementCache.allAchievements.slice(startIndex, endIndex);
      setAchievements(paginatedAchievements);
      setTotalRecords(achievementCache.allAchievements.length);
      setHasServerTotal(Boolean(achievementCache.hasServerTotal));
      setSuppressOptimisticNext(Boolean(achievementCache.suppressOptimisticNext));
      lastCallParamsRef.current = currentParams;
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
      }
      loadingDelayTimerRef.current = setTimeout(() => {
        setIsLoading(false);
        loadingDelayTimerRef.current = null;
      }, 200);
      return;
    }

    const hasListCache =
      achievementCache.achievements !== null && !achievementCache.firstLoad && !isSearchChange;
    if (
      hasListCache &&
      isInitialMountRef.current &&
      currentParams.page === achievementCache.page &&
      currentParams.search === (achievementCache.lastSearch || '')
    ) {
      let cachedAchievements = achievementCache.achievements;
      if (achievementCache.allAchievements && achievementCache.allAchievements.length > itemsPerPage) {
        const startIndex = (currentParams.page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        cachedAchievements = achievementCache.allAchievements.slice(startIndex, endIndex);
      }
      setAchievements(cachedAchievements);
      setCurrentPage(achievementCache.page);
      setTotalRecords(achievementCache.totalRecords);
      setHasServerTotal(Boolean(achievementCache.hasServerTotal));
      setSuppressOptimisticNext(Boolean(achievementCache.suppressOptimisticNext));
      lastCallParamsRef.current = currentParams;
      isInitialMountRef.current = false;
      return;
    }

    const isSameParams =
      lastParams.page === currentParams.page && lastParams.search === currentParams.search;

    if (isSameParams) {
      if (isInitialMountRef.current) {
        if (lastParams.page !== null) {
          return;
        }
      } else {
        return;
      }
    }

    if (isLoadingRef.current) {
      return;
    }

    lastCallParamsRef.current = currentParams;
    isLoadingRef.current = true;

    loadAchievements(currentPage, debouncedSearch).finally(() => {
      isLoadingRef.current = false;
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    return () => {
      if (loadingDelayTimerRef.current) {
        clearTimeout(loadingDelayTimerRef.current);
      }
    };
  }, []);

  // Calculate totalPages based on totalRecords (and allow probing next page when total is unknown)
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const canProbeNextPage =
    !hasServerTotal &&
    !suppressOptimisticNext &&
    achievements.length === itemsPerPage &&
    totalRecords > 0;
  const visibleTotalPages = canProbeNextPage ? Math.max(totalPages, currentPage + 1) : totalPages;

  // Load achievements from API (same server/client pagination strategy as module list)
  const loadAchievements = async (pageOverride = undefined, searchOverride = undefined) => {
    const pageToUse = pageOverride != null ? pageOverride : currentPage;
    const searchToUse = searchOverride != null ? searchOverride : debouncedSearch;

    const isFirstLoad = achievementCache.firstLoad;
    const isSearchChange = searchToUse !== (achievementCache.lastSearch || '');
    const isPageChange = pageToUse !== (achievementCache.page || 1);
    if (isSearchChange) {
      achievementCache.suppressOptimisticNext = false;
      setSuppressOptimisticNext(false);
    }

    if (isFirstLoad || isSearchChange || isPageChange) {
      setIsLoading(true);
    }

    try {
      const response = await fetchAchievementList(pageToUse, itemsPerPage, searchToUse, {});

      setIsLoading(false);

      let achievementsData = extractAchievementRows(response);

      const totalRaw =
        response?.total ??
        response?.data?.total ??
        response?.data?.totalDocs ??
        response?.data?.count ??
        response?.data?.pagination?.total ??
        response?.pagination?.total ??
        response?.data?.pagination?.totalRecords ??
        response?.data?.pagination?.totalCount ??
        response?.data?.data?.total ??
        response?.data?.data?.pagination?.total ??
        response?.result?.total ??
        response?.result?.pagination?.total ??
        null;

      let total =
        totalRaw !== null && totalRaw !== undefined && totalRaw !== ''
          ? Number(totalRaw)
          : null;
      if (total !== null && Number.isNaN(total)) {
        total = null;
      }

      if (total === null || total === undefined) {
        if (achievementsData.length > itemsPerPage) {
          total = achievementsData.length;
        } else if (achievementsData.length === itemsPerPage) {
        
          total = pageToUse * itemsPerPage;
        } else if (achievementsData.length > 0) {
          total = (pageToUse - 1) * itemsPerPage + achievementsData.length;
        } else {
          total = 0;
        }
      }

      const mapAchievement = (achievement) => ({
        id: achievement._id || achievement.id,
        title: achievement.name || achievement.title,
        description: achievement.description || '',
        modulesCompleted: achievement.module_completion_threshold || achievement.modulesCompleted || 0,
        logo_url: achievement.logo_url || '',
        order: achievement.order || 0,
      });

      const mappedAll = achievementsData.map(mapAchievement);

      const topTotal =
        totalRaw !== null && totalRaw !== undefined && totalRaw !== ''
          ? Number(totalRaw)
          : null;
      const topTotalPages =
        response?.totalPages ??
        response?.data?.totalPages ??
        response?.pagination?.totalPages ??
        response?.data?.pagination?.totalPages;

      const numericTopTotal =
        topTotal !== null && !Number.isNaN(topTotal) ? topTotal : null;

      const hasServerPagination =
        (topTotalPages !== undefined && topTotalPages !== null) ||
        (numericTopTotal !== null && numericTopTotal !== achievementsData.length);
      const hasKnownTotalFromServer =
        (topTotalPages !== undefined && topTotalPages !== null) || numericTopTotal !== null;

      let displayList = mappedAll;
      const useClientPagination = !hasServerPagination && achievementsData.length > itemsPerPage;

      if (useClientPagination) {
        const startIndex = (pageToUse - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        displayList = mappedAll.slice(startIndex, endIndex);
        total = mappedAll.length;
      }

      // Do not leave the user on a blank page (e.g. deleted last row on last page)
      if (displayList.length === 0 && pageToUse > 1) {
        // We confirmed this "next page" is empty; hide optimistic next until data changes.
        achievementCache.suppressOptimisticNext = true;
        setSuppressOptimisticNext(true);
        let targetPage;
        if (useClientPagination) {
          const maxPage = Math.max(1, Math.ceil(mappedAll.length / itemsPerPage));
          targetPage = Math.min(pageToUse - 1, maxPage);
        } else {
          const lastPageFromTotal =
            total > 0 ? Math.ceil(total / itemsPerPage) : 0;
          targetPage =
            lastPageFromTotal > 0
              ? Math.min(pageToUse - 1, lastPageFromTotal)
              : pageToUse - 1;
        }
        if (targetPage >= 1 && targetPage < pageToUse) {
          return loadAchievements(targetPage, searchToUse);
        }
      }

      setAchievements(displayList);
      setTotalRecords(total);
      setHasServerTotal(hasKnownTotalFromServer);
      if (displayList.length > 0 && pageToUse > 1) {
        achievementCache.suppressOptimisticNext = false;
        setSuppressOptimisticNext(false);
      }

      if (pageOverride != null && pageToUse !== currentPage) {
        setCurrentPage(pageToUse);
      }

      achievementCache.allAchievements = useClientPagination ? mappedAll : null;
      achievementCache.achievements = displayList;
      achievementCache.useClientPagination = useClientPagination;
      achievementCache.page = pageToUse;
      achievementCache.totalRecords = total;
      achievementCache.hasServerTotal = hasKnownTotalFromServer;
      achievementCache.suppressOptimisticNext =
        displayList.length === 0 && pageToUse > 1
          ? true
          : achievementCache.suppressOptimisticNext;
      achievementCache.lastSearch = searchToUse;
      achievementCache.firstLoad = false;

      lastCallParamsRef.current = { page: pageToUse, search: searchToUse };
    } catch (error) {
      console.error('Failed to load achievements:', error);
      setAchievements([]);
      setTotalRecords(0);
      setHasServerTotal(false);
      setSuppressOptimisticNext(false);
      achievementCache.allAchievements = null;
      achievementCache.useClientPagination = false;
      achievementCache.hasServerTotal = false;
      achievementCache.suppressOptimisticNext = false;
      Toast.error(error.message || 'Failed to load achievements. Please try again.');
      achievementCache.firstLoad = false;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch total module count
  const fetchTotalModules = async () => {
    // Prevent duplicate calls (especially in React StrictMode)
    if (hasFetchedModulesRef.current || isFetchingModulesRef.current) {
      return;
    }
    
    hasFetchedModulesRef.current = true;
    isFetchingModulesRef.current = true;
    
    try {
      // Try fetching with a large limit to get all modules or use total from response
      const response = await fetchModuleList(1, 10000, '', {});
      
      let totalCount = 0;
      
      // Check for total count in various response structures
      if (response?.total !== undefined && response.total !== null) {
        totalCount = response.total;
      } else if (response?.data?.total !== undefined && response.data.total !== null) {
        totalCount = response.data.total;
      } else if (Array.isArray(response?.data?.data)) {
        totalCount = response.data.data.length;
      } else if (Array.isArray(response?.data)) {
        totalCount = response.data.length;
      } else if (response?.data?.list && Array.isArray(response.data.list)) {
        totalCount = response.data.list.length;
      } else if (Array.isArray(response)) {
        totalCount = response.length;
      }
      
      setTotalModules(totalCount || 0);
    } catch (error) {
      console.error('Failed to fetch module count:', error);
      setTotalModules(0);
      // Reset flag on error so it can be retried
      hasFetchedModulesRef.current = false;
    } finally {
      isFetchingModulesRef.current = false;
    }
  };

  // Fetch total modules on component mount - only once
  useEffect(() => {
    fetchTotalModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(event.target)) {
        setAddDropdownOpen(false);
      }
      if (editDropdownRef.current && !editDropdownRef.current.contains(event.target)) {
        setEditDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Pagination range calculation
  const getPaginationRange = () => {
    if (visibleTotalPages === 0) return [];

    const totalPageNumbers = 5;
    const totalBlocks = totalPageNumbers + 2;

    if (visibleTotalPages <= totalBlocks) {
      return Array.from({ length: visibleTotalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - 1, 1);
    const rightSiblingIndex = Math.min(currentPage + 1, visibleTotalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < visibleTotalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = visibleTotalPages;
    const pages = [firstPageIndex];

    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : visibleTotalPages - 1;

    if (shouldShowLeftDots) {
      pages.push('dots');
    }

    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(page);
    }

    if (shouldShowRightDots) {
      pages.push('dots');
    }

    pages.push(lastPageIndex);
    return pages;
  };

  // Custom Grid Icon Component (3 rows, 2 dots each - vertical layout)
  const GridIcon = ({ size = 18 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400"
    >
      {/* Left column - 3 dots */}
      <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
      {/* Right column - 3 dots */}
      <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
    </svg>
  );

  const SortableAchievementItem = ({ achievement, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({
      id: achievement.id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
            {index + 1}
          </div>

          <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0 overflow-hidden relative">
            {achievement.logo_url && achievement.logo_url.trim() ? (
              <img 
                src={achievement.logo_url} 
                alt={achievement.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.parentElement.querySelector('.logo-fallback');
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`logo-fallback w-full h-full flex items-center justify-center ${achievement.logo_url && achievement.logo_url.trim() ? 'hidden' : ''}`}
            >
              <span className="text-brand font-bold text-lg">
                {achievement.title ? achievement.title.charAt(0).toUpperCase() : 'A'}
              </span>
            </div>
          </div>

          <p className="text-black font-medium flex-1 min-w-0 break-words line-clamp-2">
            {achievement.title}
          </p>
        </div>

        <div className="flex-shrink-0 ml-2">
          <GridIcon size={18} />
        </div>
      </div>
    );
  };

  // Get used module numbers from existing achievements
  // When forEdit = true: Returns only the exact values used by other achievements (for both add and edit modals)
  // When forEdit = false: Returns all numbers from 1 to max (legacy behavior, not currently used)
  const getUsedModuleNumbers = (excludeAchievementId = null, forEdit = false) => {
    const filteredAchievements = achievements.filter(
      achievement => achievement.id !== excludeAchievementId && achievement.modulesCompleted > 0
    );
    
    if (filteredAchievements.length === 0) {
      return [];
    }
    
    if (forEdit) {
      // Return only exact values used by other achievements
      return filteredAchievements.map(a => a.modulesCompleted);
    } else {
      // Legacy: return all numbers from 1 to max (not currently used)
      const maxModulesCompleted = Math.max(
        ...filteredAchievements.map(a => a.modulesCompleted)
      );
      
      const disabledNumbers = [];
      for (let i = 1; i <= maxModulesCompleted; i++) {
        disabledNumbers.push(i);
      }
      
      return disabledNumbers;
    }
  };

  const handleAddAchievement = () => {
    setNewAchievement({
      title: '',
      description: '',
      modulesCompleted: 0,
      logo_url: '',
      order: 1,
    });
    setAddLogoSource('url');
    setAddLogoFile(null);
    setAddLogoUploadedUrl('');
    setUploadingAddLogo(false);
    setAddLogoUploadError('');
    setAddModulesCompletedError('');
    setAddTitleError('');
    setAddDescriptionError('');
    setAddImageError('');
    setAddDropdownOpen(false);
    setShowAddModal(true);
  };
  
  // Handle file upload for add modal
  const handleAddLogoUpload = async (file) => {
    if (!file) return;

    setUploadingAddLogo(true);
    setAddLogoUploadError('');
    setAddImageError('');

    try {
      const uploadedUrl = await uploadImage(file);
      setAddLogoFile(file);
      setAddLogoUploadedUrl(uploadedUrl);
      setNewAchievement({ ...newAchievement, logo_url: uploadedUrl });
      setAddLogoUploadError('');
      setAddImageError('');
    } catch (error) {
      setAddLogoUploadError(error.message || 'Failed to upload image. Please try again.');
      setAddLogoFile(null);
      setAddLogoUploadedUrl('');
      setNewAchievement({ ...newAchievement, logo_url: '' });
    } finally {
      setUploadingAddLogo(false);
    }
  };
  
  // Handle file upload for edit modal
  const handleEditLogoUpload = async (file) => {
    if (!file) return;

    setUploadingEditLogo(true);
    setEditLogoUploadError('');
    setEditImageError('');

    try {
      const uploadedUrl = await uploadImage(file);
      setEditLogoFile(file);
      setEditLogoUploadedUrl(uploadedUrl);
      setEditingAchievement({ ...editingAchievement, logo_url: uploadedUrl });
      setEditLogoUploadError('');
      setEditImageError('');
    } catch (error) {
      setEditLogoUploadError(error.message || 'Failed to upload image. Please try again.');
      setEditLogoFile(null);
      setEditLogoUploadedUrl('');
      setEditingAchievement({ ...editingAchievement, logo_url: editingAchievement.logo_url || '' });
    } finally {
      setUploadingEditLogo(false);
    }
  };

  const handleAddAchievementSubmit = async () => {
    // Validate fields
    let hasError = false;
    if (!newAchievement.title.trim()) {
      setAddTitleError('Achievement Title is required');
      hasError = true;
    } else {
      setAddTitleError('');
    }
    if (!newAchievement.description.trim()) {
      setAddDescriptionError('Description is required');
      hasError = true;
    } else {
      setAddDescriptionError('');
    }
    // Validate Completion Requirement
    const modulesCompletedValue = newAchievement.modulesCompleted || 0;
    if (modulesCompletedValue < 1 || (totalModules > 0 && modulesCompletedValue > totalModules)) {
      if (totalModules > 0) {
        setAddModulesCompletedError(`Completion Requirement must be between 1 and ${totalModules}`);
      } else {
        setAddModulesCompletedError('Completion Requirement must be at least 1');
      }
      hasError = true;
    } else {
      setAddModulesCompletedError('');
    }
    // Validate Achievement Image
    const logoUrl = addLogoSource === 'file' 
      ? (addLogoUploadedUrl || newAchievement.logo_url || '')
      : (newAchievement.logo_url || '');
    if (!logoUrl || !logoUrl.trim()) {
      setAddImageError('Achievement Image is required');
      hasError = true;
    } else {
      setAddImageError('');
    }
    if (hasError) {
      return;
    }

    setAdding(true);
    try {
      // Use uploaded URL if file was uploaded, otherwise use the URL input
      
      const achievementData = {
        name: newAchievement.title,
        description: newAchievement.description,
        logo_url: logoUrl,
        module_completion_threshold: newAchievement.modulesCompleted !== '' && newAchievement.modulesCompleted !== null && newAchievement.modulesCompleted !== undefined ? parseInt(newAchievement.modulesCompleted) : (newAchievement.modulesCompleted === 0 ? 0 : 1),
        order: newAchievement.order || 1,
      };
      
      await createAchievement(achievementData);
      setShowAddModal(false);
      setAddDropdownOpen(false);
      setNewAchievement({
        title: '',
        description: '',
        modulesCompleted: 0,
        logo_url: '',
        order: 1,
      });
      setAddLogoSource('url');
      setAddLogoFile(null);
      setAddLogoUploadedUrl('');
      setUploadingAddLogo(false);
      setAddLogoUploadError('');
      setAddImageError('');
      // Clear cache and reset params to force fresh reload
      clearAchievementListCache();
      lastCallParamsRef.current = { page: null, search: null };
      // If this page was full, the new row usually lands on the next page — open it
      const pageWasFull = achievements.length >= itemsPerPage;
      const pageToShow = pageWasFull ? currentPage + 1 : currentPage;
      await loadAchievements(pageWasFull ? pageToShow : undefined);
      // Toast.success('Achievement added successfully!');
    } catch (err) {
      Toast.error(err.message || 'Failed to add achievement. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  const cancelAddAchievement = () => {
    setShowAddModal(false);
    setNewAchievement({
      title: '',
      description: '',
      modulesCompleted: 0,
      logo_url: '',
      order: 1,
    });
    setAddLogoSource('url');
    setAddLogoFile(null);
    setAddLogoUploadedUrl('');
    setUploadingAddLogo(false);
    setAddLogoUploadError('');
    setAddModulesCompletedError('');
    setAddTitleError('');
    setAddDescriptionError('');
    setAddImageError('');
  };

  const handleSaveAchievementOrder = async () => {
    setReorderSaveLoading(true);
    try {
      const payload = reorderAchievements.map((a, index) => ({
        achievement_id: String(a.id || a._id),
        order: index + 1,
      }));

      // Update each achievement's order via API
      const updatePromises = payload.map(async (item) => {
        const achievement = reorderAchievements.find(
          (a) => String(a.id || a._id) === item.achievement_id
        );
        if (achievement) {
          return updateAchievement({
            achievement_id: item.achievement_id,
            name: achievement.title || achievement.name,
            description: achievement.description || '',
            logo_url: achievement.logo_url || '',
            module_completion_threshold: achievement.modulesCompleted || achievement.module_completion_threshold || 1,
            order: item.order,
            is_active: achievement.is_active !== undefined ? achievement.is_active : true,
          });
        }
      });

      await Promise.all(updatePromises);
      setShowReorderModal(false);
      Toast.success('Achievement order updated successfully');
      // Clear cache and reset params to force fresh reload
      clearAchievementListCache();
      lastCallParamsRef.current = { page: null, search: null };
      // Same full-page list loader as after add (loadAchievements often skips isLoading when page unchanged)
      setIsLoading(true);
      await loadAchievements();
    } catch (err) {
      console.error(err);
      Toast.error('Failed to save achievement order');
    } finally {
      setReorderSaveLoading(false);
      setIsLoading(false);
    }
  };

  // Cleanup: Restore scrolling when modal closes
  useEffect(() => {
    if (!showReorderModal) {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [showReorderModal]);

  return (
    <div className="flex h-screen bg-primary overflow-hidden">
      <style>{`
        .achievement-textarea::placeholder {
          line-height: 2.5;
          transform: translateY(1px);
        }
      `}</style>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-primary sticky top-2">
          <div className="flex items-center justify-between p-4 px-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-black/5 text-black"
              >
                <HiMenu className="w-6 h-6" />
              </button>
              <h1 className="text-xl sm:text-3xl font-black text-black">
                Achievement
              </h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 pb-12 overflow-y-auto">
          <div className="max-w-[1700px] mx-auto">
            {/* Search Bar */}
            <div className="mb-5 md:mb-4 mt-4 md:mt-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
                <div className="flex flex-col gap-2 w-full md:w-auto">
                  <div className="relative w-full md:w-40">
                    <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 md:py-1.5 text-sm border border-[#ECECEC] focus:outline-none focus:border-gray-300 bg-white"
                      style={{ borderRadius: '12px' }}
                    />
                  </div>

                  {/* Mobile Row: Buttons in one line */}
                  <div className="flex items-center justify-between gap-2 w-full md:hidden">
                    <button
                      onClick={handleAddAchievement}
                      className="add-achievement-btn-mobile flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                    >
                      <span className="text-base font-bold">+</span>
                      <span>Add Achievement</span>
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          // Load all achievements for reordering (use a large pageSize to get all)
                          const response = await fetchAchievementList(1, 1000, '', {});
                          let allAchievementsData = [];
                          
                          if (Array.isArray(response)) {
                            allAchievementsData = response;
                          } else if (Array.isArray(response?.data)) {
                            allAchievementsData = response.data;
                          } else if (Array.isArray(response?.data?.data)) {
                            allAchievementsData = response.data.data;
                          } else if (response?.data?.list && Array.isArray(response.data.list)) {
                            allAchievementsData = response.data.list;
                          }

                          const mappedAchievements = allAchievementsData.map((achievement) => ({
                            id: achievement._id || achievement.id,
                            title: achievement.name || achievement.title,
                            description: achievement.description || '',
                            modulesCompleted: achievement.module_completion_threshold || achievement.modulesCompleted || 0,
                            logo_url: achievement.logo_url || '',
                            order: achievement.order || 0,
                            is_active: achievement.is_active !== undefined ? achievement.is_active : true,
                          }));

                          setReorderAchievements(mappedAchievements);
                          setShowReorderModal(true);
                        } catch (error) {
                          Toast.error('Failed to load achievements for reordering: ' + (error.message || 'Unknown error'));
                        }
                      }}
                      className="edit-order-btn flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                    >
                      {/* <img src={EditOrderIcon} alt="Edit Order" className="w-5 h-5" style={{ filter: 'brightness(0) invert(1)' }} /> */}
                      <span>Edit Order</span>
                    </button>
                  </div>
                </div>

                {/* Desktop/Tablet Row: Edit Order & Add Achievement */}
                <div className="hidden md:flex items-center justify-end gap-2 w-full md:w-auto">
                  <button
                    onClick={async () => {
                      try {
                        // Load all achievements for reordering (use a large pageSize to get all)
                        const response = await fetchAchievementList(1, 1000, '', {});
                        let allAchievementsData = [];
                        
                        if (Array.isArray(response)) {
                          allAchievementsData = response;
                        } else if (Array.isArray(response?.data)) {
                          allAchievementsData = response.data;
                        } else if (Array.isArray(response?.data?.data)) {
                          allAchievementsData = response.data.data;
                        } else if (response?.data?.list && Array.isArray(response.data.list)) {
                          allAchievementsData = response.data.list;
                        }

                        const mappedAchievements = allAchievementsData.map((achievement) => ({
                          id: achievement._id || achievement.id,
                          title: achievement.name || achievement.title,
                          description: achievement.description || '',
                          modulesCompleted: achievement.module_completion_threshold || achievement.modulesCompleted || 0,
                          logo_url: achievement.logo_url || '',
                          order: achievement.order || 0,
                        }));

                        setReorderAchievements(mappedAchievements);
                        setShowReorderModal(true);
                      } catch (error) {
                        Toast.error('Failed to load achievements for reordering: ' + (error.message || 'Unknown error'));
                      }
                    }}
                    className="edit-order-btn flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                  >
                    {/* <img src={EditOrderIcon} alt="Edit Order" className="w-5 h-5" style={{ filter: 'brightness(0) invert(1)' }} /> */}
                    <span>Edit Order</span>
                  </button>
                  <button
                    onClick={handleAddAchievement}
                    className="add-achievement-btn-desktop flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                  >
                    <span className="text-base font-bold">+</span>
                    <span>Add Achievement</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Achievement Cards Grid */}
            {isLoading ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
                <div className="flex items-center gap-3" role="status" aria-live="polite">
                  <svg
                    className="w-12 h-12 animate-spin"
                    viewBox="0 0 100 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#E9D5FF" />
                        <stop offset="25%" stopColor="#C084FC" />
                        <stop offset="50%" stopColor="#A855F7" />
                        <stop offset="75%" stopColor="#9333EA" />
                        <stop offset="100%" stopColor="#6B21A8" />
                      </linearGradient>
                    </defs>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="url(#purple-gradient)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="60 200"
                      fill="none"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                </div>
              </div>
            ) : achievements.length === 0 ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center">
                  {String(debouncedSearch || '').trim().length > 0 ? (
                    <>
                      <p className="text-sm text-gray-500 mb-6">No achievements found. Try a different keyword.</p>
                    </>
                  ) : (
                    <>   
                      <div className="mb-6">
                      <svg
                          className="w-24 h-24 mx-auto text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                  </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        No achievements added yet
                      </h3>
                      <p className="text-sm text-gray-500 mb-6">
                        Start by adding the first achievement
                      </p>
                      <button
                        onClick={handleAddAchievement}
                        className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90"
                      >
                        <span className="text-base">+</span>
                        <span>Add Achievement</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : ( 
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-4">
                {achievements.map((achievement) => (
                <div key={achievement.id} className="bg-white rounded-xl p-4 sm:p-5 transition-shadow relative overflow-hidden" style={{ boxShadow: '0px 3px 8px 0px #93939340' }}>
                  {/* Row 1: Icon, Title with Edit/Delete buttons */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Achievement Icon/Logo */}
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0 overflow-hidden relative">
                        {achievement.logo_url && achievement.logo_url.trim() ? (
                          <img 
                            src={achievement.logo_url} 
                            alt={achievement.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.querySelector('.logo-fallback');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`logo-fallback w-full h-full flex items-center justify-center ${achievement.logo_url && achievement.logo_url.trim() ? 'hidden' : ''}`}
                        >
                          <span className="text-brand font-bold text-sm sm:text-base">
                            {achievement.title ? achievement.title.charAt(0).toUpperCase() : 'A'}
                          </span>
                        </div>
                      </div>
                      {/* Separator Line */}
                      <div className="h-10 w-px bg-[#F2E2F7] flex-shrink-0"></div>
                      {/* Achievement Title */}
                      <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate flex-1 min-w-0">
                        {achievement.title}
                      </h3>
                    </div>
                    {/* Edit and Delete Buttons */}
                    <div className="flex items-center line-clamp-2  gap-2 flex-shrink-0 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const logoUrl = achievement.logo_url || '';
                          setEditingAchievement({
                            id: achievement.id,
                            title: achievement.title,
                            description: achievement.description,
                            modulesCompleted: achievement.modulesCompleted || 0,
                            logo_url: logoUrl,
                            order: achievement.order || 1,
                            is_active: achievement.is_active !== undefined ? achievement.is_active : true,
                          });
                          // Set logo source based on whether URL exists
                          setEditLogoSource(logoUrl ? 'url' : 'url');
                          setEditLogoFile(null);
                          setEditLogoUploadedUrl('');
                          setUploadingEditLogo(false);
                          setEditLogoUploadError('');
                          setEditModulesCompletedError('');
                          setEditTitleError('');
                          setEditDescriptionError('');
                          setEditImageError('');
                          setEditDropdownOpen(false);
                          setShowEditModal(true);
                        }}
                        className="w-8 h-8 text-sm font-bold rounded-lg transition-colors hover:opacity-90 text-brand bg-[#EFEBF3] flex items-center justify-center"
                      >
                        <HiPencil className="w-4 h-4" />
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAchievementToDelete({
                            id: achievement.id,
                            title: achievement.title,
                          });
                          setShowDeleteModal(true);
                        }}
                        className="w-8 h-8 font-bold text-sm rounded-lg transition-colors hover:opacity-90 text-[#F76464] bg-[#FFF0F0] flex items-center justify-center"
                      >
                        <HiTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Row 2: Summary (Key-Value) */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500 font-semibold">Completion Requirement</span>
                    <span className="text-sm font-semibold text-gray-900">{achievement.modulesCompleted}</span>
                  </div>

                  {/* Separator Line */}
                  <div className="h-px bg-[#ECECEC] my-2 "></div>

                  {/* Description Section */}
                  <div>
                    <p className="text-xs sm:text-sm font-semibold  text-gray-500 mb-1">Description:</p>
                    <p className="text-xs sm:text-sm line-clamp-2 text-gray-900">
                      {achievement.description}
                    </p>
                  </div>
                </div>
              ))}
              </div>

            {/* Pagination (show when there is data; next stays disabled unless another page exists) */}
            {totalRecords > 0 && visibleTotalPages >= 1 && (
              <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((pageNum, idx) =>
                      pageNum === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                            pageNum === currentPage
                              ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                              : 'text-gray-900 hover:bg-gray-100'
                          }`}
                          aria-current={pageNum === currentPage ? 'page' : undefined}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, visibleTotalPages))}
                    disabled={currentPage >= visibleTotalPages}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit Achievement Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Edit Achievement</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Achievement Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingAchievement.title}
                  onChange={(e) => {
                    setEditingAchievement({ ...editingAchievement, title: e.target.value });
                    if (editTitleError) setEditTitleError('');
                  }}
                  placeholder="Enter achievement title"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    editTitleError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editTitleError && (
                  <p className="mt-1 text-xs text-red-500">{editTitleError}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                Completion Requirement <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={editDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setEditDropdownOpen(!editDropdownOpen)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none flex items-center justify-between ${
                      editModulesCompletedError ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <span>{editingAchievement.modulesCompleted > 0 ? editingAchievement.modulesCompleted : 0}</span>
                    <HiChevronDown className={`w-4 h-4 transition-transform ${editDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {editDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-[150px] overflow-y-auto">
                      {(() => {
                        // For edit modal: get only exact values used by other achievements
                        const usedExactNumbers = getUsedModuleNumbers(editingAchievement.id, true);
                        const options = new Set();
                        if (totalModules > 0) {
                          // Add options from 1 to totalModules
                          for (let i = 1; i <= totalModules; i++) {
                            options.add(i);
                          }
                        } else {
                          // If modules are still loading, show at least option 1
                          options.add(1);
                        }
                        // If current value is outside the range, add it to show current invalid value
                        const currentValue = editingAchievement.modulesCompleted;
                        if (currentValue > 0 && (!totalModules || currentValue > totalModules) && !options.has(currentValue)) {
                          options.add(currentValue);
                        }
                        // Sort options and render
                        const sortedOptions = Array.from(options).sort((a, b) => a - b);
                        
                        return sortedOptions.map((num) => {
                          // Disable only exact values used by other achievements
                          const isUsedByOthers = usedExactNumbers.includes(num);
                          const isDisabled = isUsedByOthers;
                          const isSelected = editingAchievement.modulesCompleted === num;
                          
                          return (
                            <button
                              key={num}
                              type="button"
                              disabled={isDisabled && !isSelected}
                              onClick={() => {
                                if (isDisabled && !isSelected) return;
                                const numValue = parseInt(num, 10);
                                
                                // Validation: must be between 1 and totalModules
                                if (numValue < 1 || (totalModules > 0 && numValue > totalModules)) {
                                  if (totalModules > 0) {
                                    setEditModulesCompletedError(`Completion Requirement must be between 1 and ${totalModules}`);
                                  } else {
                                    setEditModulesCompletedError('Completion Requirement must be at least 1');
                                  }
                                } else {
                                  setEditModulesCompletedError('');
                                }
                                
                                setEditingAchievement({ ...editingAchievement, modulesCompleted: numValue });
                                setEditDropdownOpen(false);
                              }}
                              className={`w-full px-3 py-2 text-sm text-left ${
                                isDisabled && !isSelected
                                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                                  : isSelected
                                  ? 'bg-brand/10 text-brand font-semibold hover:bg-brand/20'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {num}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
                {editModulesCompletedError && (
                  <p className="mt-1 text-xs text-red-500">{editModulesCompletedError}</p>
                )}
                {totalModules === 0 && (
                  <p className="mt-1 text-xs text-gray-500">Loading modules...</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editingAchievement.description}
                  onChange={(e) => {
                    setEditingAchievement({ ...editingAchievement, description: e.target.value });
                    if (editDescriptionError) setEditDescriptionError('');
                  }}
                  placeholder="Enter achievement description"
                  rows="1"
                  className={` w-full px-3 py-2 text-sm border rounded-lg focus:outline-none resize-none ${
                    editDescriptionError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editDescriptionError && (
                  <p className="mt-1 text-xs text-red-500">{editDescriptionError}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Achievement Image
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 group relative">
                      <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                      <span className={`absolute right-0 top-0  -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                        editLogoSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'
                      }`}>
                        {editLogoSource === 'url' 
                          ? 'Switch left to choose a file from your device' 
                          : 'Switch right to paste an image URL.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditLogoSource(editLogoSource === 'url' ? 'file' : 'url')}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${editLogoSource === 'url' ? 'bg-brand' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${editLogoSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                      />
                    </button>
                  </div>
                </div>
                {editLogoSource === 'url' ? (
                  <div>
                    <input
                      type="url"
                      value={editingAchievement.logo_url}
                      onChange={(e) => {
                        setEditingAchievement({ ...editingAchievement, logo_url: e.target.value });
                        if (editImageError) setEditImageError('');
                      }}
                      placeholder="https://example.com/achievements/first-steps.png"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-transparent ${
                        editImageError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {editingAchievement.logo_url && (
                      <div className="mt-2">
                        <img
                          src={editingAchievement.logo_url}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleEditLogoUpload(file);
                        }
                      }}
                      disabled={uploadingEditLogo}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${
                        editLogoUploadError || editImageError ? 'border-red-500' : 'border-gray-300'
                      } ${uploadingEditLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {uploadingEditLogo && (
                      <div className="mt-2 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-xs text-gray-600">Uploading image...</span>
                      </div>
                    )}
                    {(editLogoUploadedUrl || editLogoFile || editingAchievement.logo_url) && !uploadingEditLogo && (
                      <div className="mt-2">
                        <img
                          src={editLogoUploadedUrl || (editLogoFile ? URL.createObjectURL(editLogoFile) : editingAchievement.logo_url)}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    {editLogoUploadError && (
                      <p className="mt-1 text-xs text-red-500">{editLogoUploadError}</p>
                    )}
                    {editImageError && !editLogoUploadError && (
                      <p className="mt-1 text-xs text-red-500">{editImageError}</p>
                    )}
                  </div>
                )}
                {editImageError && editLogoSource === 'url' && (
                  <p className="mt-1 text-xs text-red-500">{editImageError}</p>
                )}
              </div>
              {/* <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  value={editingAchievement.order}
                  onChange={(e) => setEditingAchievement({ ...editingAchievement, order: parseInt(e.target.value) || 1 })}
                  placeholder="Enter order number"
                  min="1"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                />
              </div> */}
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingAchievement.is_active}
                    onChange={(e) => setEditingAchievement({ ...editingAchievement, is_active: e.target.checked })}
                    className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
                  />
                  <span className="text-xs font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditDropdownOpen(false);
                  setEditingAchievement({
                    id: null,
                    title: '',
                    description: '',
                    modulesCompleted: 0,
                    logo_url: '',
                    order: 1,
                    is_active: true,
                  });
                  setEditLogoSource('url');
                  setEditLogoFile(null);
                  setEditLogoUploadedUrl('');
                  setUploadingEditLogo(false);
                  setEditLogoUploadError('');
                  setEditModulesCompletedError('');
                  setEditTitleError('');
                  setEditDescriptionError('');
                  setEditImageError('');
                }}
                disabled={editing || uploadingEditLogo}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Validate fields
                  let hasError = false;
                  if (!editingAchievement.title.trim()) {
                    setEditTitleError('Achievement Title is required');
                    hasError = true;
                  } else {
                    setEditTitleError('');
                  }
                  if (!editingAchievement.description.trim()) {
                    setEditDescriptionError('Description is required');
                    hasError = true;
                  } else {
                    setEditDescriptionError('');
                  }
                  // Validate Completion Requirement
                  const modulesCompletedValue = editingAchievement.modulesCompleted || 0;
                  if (modulesCompletedValue < 1 || (totalModules > 0 && modulesCompletedValue > totalModules)) {
                    if (totalModules > 0) {
                      setEditModulesCompletedError(`Completion Requirement must be between 1 and ${totalModules}`);
                    } else {
                      setEditModulesCompletedError('Completion Requirement must be at least 1');
                    }
                    hasError = true;
                  } else {
                    setEditModulesCompletedError('');
                  }
                  // Validate Achievement Image
                  const logoUrl = editLogoSource === 'file' 
                    ? (editLogoUploadedUrl || editingAchievement.logo_url || '')
                    : (editingAchievement.logo_url || '');
                  if (!logoUrl || !logoUrl.trim()) {
                    setEditImageError('Achievement Image is required');
                    hasError = true;
                  } else {
                    setEditImageError('');
                  }
                  if (hasError) {
                    return;
                  }

                  setEditing(true);
                  try {
                    // Use uploaded URL if file was uploaded, otherwise use the URL input
                    
                    const achievementData = {
                      achievement_id: editingAchievement.id,
                      name: editingAchievement.title,
                      description: editingAchievement.description,
                      logo_url: logoUrl,
                      module_completion_threshold: editingAchievement.modulesCompleted !== '' && editingAchievement.modulesCompleted !== null && editingAchievement.modulesCompleted !== undefined ? parseInt(editingAchievement.modulesCompleted) : (editingAchievement.modulesCompleted === 0 ? 0 : 1),
                      order: editingAchievement.order || 1,
                      is_active: editingAchievement.is_active !== undefined ? editingAchievement.is_active : true,
                    };
                    
                    await updateAchievement(achievementData);
                    setShowEditModal(false);
                    setEditDropdownOpen(false);
                    setEditingAchievement({
                      id: null,
                      title: '',
                      description: '',
                      modulesCompleted: 0,
                      logo_url: '',
                      order: 1,
                      is_active: true,
                    });
                    setEditLogoSource('url');
                    setEditLogoFile(null);
                    setEditLogoUploadedUrl('');
                    setUploadingEditLogo(false);
                    setEditLogoUploadError('');
                    setEditModulesCompletedError('');
                    setEditTitleError('');
                    setEditDescriptionError('');
                    setEditImageError('');
                    // Clear cache and reset params to force fresh reload
                    clearAchievementListCache();
                    lastCallParamsRef.current = { page: null, search: null };
                    // Reload achievements list
                    await loadAchievements();
                    // Toast.success('Achievement updated successfully!');
                  } catch (err) {
                    Toast.error(err.message || 'Failed to update achievement. Please try again.');
                  } finally {
                    setEditing(false);
                  }
                }}
                disabled={editing || uploadingEditLogo}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {editing ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update Achievement'
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteModal && achievementToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center  flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800">Delete Achievement</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to delete the achievement?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setAchievementToDelete(null);
                }}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await deleteAchievement(achievementToDelete.id);
                    setShowDeleteModal(false);
                    setAchievementToDelete(null);

                    // Immediately update pagination so blank trailing page disappears right after delete.
                    let projectedTotal = Math.max(0, Number(totalRecords || 0) - 1);
                    const projectedLastPage = Math.max(1, Math.ceil(projectedTotal / itemsPerPage));
                    const willEmptyCurrentPage = currentPage > 1 && achievements.length <= 1;
                    const pageAfterDelete = willEmptyCurrentPage
                      ? currentPage - 1
                      : Math.min(currentPage, projectedLastPage);
                    let shouldHideOptimisticNext = willEmptyCurrentPage;

                    // When server does not provide total, probe next page once to decide instantly.
                    if (!hasServerTotal) {
                      try {
                        const nextPageProbe = await fetchAchievementList(
                          pageAfterDelete + 1,
                          itemsPerPage,
                          debouncedSearch,
                          {}
                        );
                        const nextRows = extractAchievementRows(nextPageProbe);
                        if (nextRows.length > 0) {
                          shouldHideOptimisticNext = false;
                          projectedTotal = Math.max(projectedTotal, pageAfterDelete * itemsPerPage + 1);
                        } else {
                          shouldHideOptimisticNext = true;
                        }
                      } catch (probeError) {
                        // If probe fails, keep current fallback behavior without blocking delete UX.
                      }
                    } else if (projectedTotal % itemsPerPage === 0) {
                      shouldHideOptimisticNext = true;
                    }
                    setTotalRecords(projectedTotal);
                    if (currentPage !== pageAfterDelete) {
                      setCurrentPage(pageAfterDelete);
                    }
                    // Hide optimistic next page when delete makes trailing page invalid.
                    if (shouldHideOptimisticNext) {
                      achievementCache.suppressOptimisticNext = true;
                      setSuppressOptimisticNext(true);
                    }

                    // Clear cache and reset params to force fresh reload (loadAchievements clamps blank pages)
                    clearAchievementListCache({
                      keepSuppressOptimisticNext: shouldHideOptimisticNext,
                    });
                    if (shouldHideOptimisticNext) {
                      achievementCache.suppressOptimisticNext = true;
                      setSuppressOptimisticNext(true);
                    }
                    lastCallParamsRef.current = { page: null, search: null };

                    setIsLoading(true);
                    await loadAchievements(pageAfterDelete, debouncedSearch);
                    // Toast.success('Achievement deleted successfully!');
                  } catch (err) {
                    Toast.error(err.message || 'Failed to delete achievement. Please try again.');
                  } finally {
                    setDeleting(false);
                    setIsLoading(false);
                  }
                }}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Achievement Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Add New Achievement</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Achievement Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newAchievement.title}
                  onChange={(e) => {
                    setNewAchievement({ ...newAchievement, title: e.target.value });
                    if (addTitleError) setAddTitleError('');
                  }}
                  placeholder="Enter achievement title"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    addTitleError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {addTitleError && (
                  <p className="mt-1 text-xs text-red-500">{addTitleError}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                Completion Requirement<span className="text-red-500 whitespace-nowrap">*</span>
                </label>
                <div className="relative" ref={addDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setAddDropdownOpen(!addDropdownOpen)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none flex items-center justify-between ${
                      addModulesCompletedError ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <span>{newAchievement.modulesCompleted > 0 ? newAchievement.modulesCompleted : 0}</span>
                    <HiChevronDown className={`w-4 h-4 transition-transform ${addDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {addDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-[150px] overflow-y-auto">
                      {(() => {
                        // For add modal: get only exact values used by existing achievements
                        const usedExactNumbers = getUsedModuleNumbers(null, true);
                        const options = totalModules > 0 
                          ? Array.from({ length: totalModules }, (_, i) => i + 1)
                          : [];
                        
                        return options.map((num) => {
                          // Disable only exact values used by other achievements
                          const isDisabled = usedExactNumbers.includes(num);
                          const isSelected = newAchievement.modulesCompleted === num;
                          
                          return (
                            <button
                              key={num}
                              type="button"
                              disabled={isDisabled && !isSelected}
                              onClick={() => {
                                if (isDisabled && !isSelected) return;
                                const numValue = parseInt(num, 10);
                                
                                // Validation: must be between 1 and totalModules
                                if (numValue < 1 || (totalModules > 0 && numValue > totalModules)) {
                                  if (totalModules > 0) {
                                    setAddModulesCompletedError(`Completion Requirement must be between 1 and ${totalModules}`);
                                  } else {
                                    setAddModulesCompletedError('Completion Requirement must be at least 1');
                                  }
                                } else {
                                  setAddModulesCompletedError('');
                                }
                                
                                setNewAchievement({ ...newAchievement, modulesCompleted: numValue });
                                setAddDropdownOpen(false);
                              }}
                              className={`w-full px-3 py-2 text-sm text-left ${
                                isDisabled && !isSelected
                                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                                  : isSelected
                                  ? 'bg-brand/10 text-brand font-semibold hover:bg-brand/20'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {num}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
                {addModulesCompletedError && (
                  <p className="mt-1 text-xs text-red-500">{addModulesCompletedError}</p>
                )}
                {totalModules === 0 && (
                  <p className="mt-1 text-xs text-gray-500">Loading modules...</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newAchievement.description}
                  onChange={(e) => {
                    setNewAchievement({ ...newAchievement, description: e.target.value });
                    if (addDescriptionError) setAddDescriptionError('');
                  }}
                  placeholder="Enter achievement description"
                  rows="1"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none resize-none ${
                    addDescriptionError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {addDescriptionError && (
                  <p className="mt-1 text-xs text-red-500">{addDescriptionError}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Achievement Image <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 group relative">
                      <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                      <span className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                        addLogoSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'
                      }`}>
                        {addLogoSource === 'url' 
                          ? 'Switch left to choose a file from your device' 
                          : 'Switch right to paste an image URL.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAddLogoSource(addLogoSource === 'url' ? 'file' : 'url')}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${addLogoSource === 'url' ? 'bg-brand' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${addLogoSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                      />
                    </button>
                  </div>
                </div>
                {addLogoSource === 'url' ? (
                  <div>
                    <input
                      type="url"
                      value={newAchievement.logo_url}
                      onChange={(e) => {
                        setNewAchievement({ ...newAchievement, logo_url: e.target.value });
                        if (addImageError) setAddImageError('');
                      }}
                      placeholder="https://example.com/achievements/first-steps.png"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-transparent ${
                        addImageError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {newAchievement.logo_url && (
                      <div className="mt-2">
                        <img
                          src={newAchievement.logo_url}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleAddLogoUpload(file);
                        }
                      }}
                      disabled={uploadingAddLogo}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${
                        addLogoUploadError || addImageError ? 'border-red-500' : 'border-gray-300'
                      } ${uploadingAddLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {uploadingAddLogo && (
                      <div className="mt-2 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-xs text-gray-600">Uploading image...</span>
                      </div>
                    )}
                    {(addLogoUploadedUrl || addLogoFile || newAchievement.logo_url) && !uploadingAddLogo && (
                      <div className="mt-2">
                        <img
                          src={addLogoUploadedUrl || (addLogoFile ? URL.createObjectURL(addLogoFile) : newAchievement.logo_url)}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    {addLogoUploadError && (
                      <p className="mt-1 text-xs text-red-500">{addLogoUploadError}</p>
                    )}
                    {addImageError && !addLogoUploadError && (
                      <p className="mt-1 text-xs text-red-500">{addImageError}</p>
                    )}
                  </div>
                )}
                {addImageError && addLogoSource === 'url' && (
                  <p className="mt-1 text-xs text-red-500">{addImageError}</p>
                )}
              </div>
              {/* <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  value={newAchievement.order}
                  onChange={(e) => setNewAchievement({ ...newAchievement, order: parseInt(e.target.value) || 1 })}
                  placeholder="Enter order number"
                  min="1"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                />
              </div> */}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={cancelAddAchievement}
                disabled={adding || uploadingAddLogo}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAchievementSubmit}
                disabled={adding || uploadingAddLogo}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {adding ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  <>
                    <span className="hidden sm:block">Add Achievement</span>
                    <span className="block sm:hidden">Add</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-[#ffffff] w-full max-w-lg rounded-2xl md:ml-0 lg:ml-8 xl:ml-0 p-6 flex flex-col max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-black/60 text-lg font-semibold">
                Reorder Achievements
              </h2>
            </div>

            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop achievements to reorder them. Click <b>Save Order</b> to apply.
            </p>

            {/* Drag List */}
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={() => {
                // Prevent horizontal scrolling during drag
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                // Restore scrolling after drag
                document.body.style.overflowX = '';

                const { active, over } = event;
                if (!over || active.id === over.id) return;

                setReorderAchievements((prev) => {
                  const oldIndex = prev.findIndex(
                    (a) => a.id === active.id
                  );
                  const newIndex = prev.findIndex(
                    (a) => a.id === over.id
                  );
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                // Restore scrolling if drag is cancelled
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderAchievements.map((a) => a.id)}
                strategy={verticalListSortingStrategy}
              >
                {/* Calculate height: each item is ~72px (p-4 = 16px top/bottom + content ~40px) + gap-3 (12px) = ~84px per item, 5 items = ~420px */}
                <div className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0" style={{ maxHeight: '420px' }}>
                  {reorderAchievements.map((achievement, index) => (
                    <SortableAchievementItem
                      key={achievement.id}
                      achievement={achievement}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Footer */}
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowReorderModal(false)}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAchievementOrder}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaveLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Achievement;
